import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import {
  ActiveCell,
  cellFactory,
  ChipCell,
  DataGridConfig,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  NumberCell,
} from '~/components/DataGridCustom';

import { ArrayCell } from './ArrayCell';
import { detectColumnType } from './columnTypeDetector';
import { ObjectCell } from './ObjectCell';

// Since the result structure is dynamic, we'll use a generic type
type CypherResultRow = Record<string, unknown>;

export const useSupplierText2CypherResultDataGrid = (results: CypherResultRow[]): DataGridConfig<CypherResultRow> => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'SupplierText2CypherResultDataGrid.column',
  });

  return useMemo(() => {
    if (!results.length || typeof results[0] !== 'object') {
      return {
        sortModel: [],
        columnVisibilityModel: {},
        columns: [],
      } as DataGridConfig<CypherResultRow>;
    }

    const createColumn = gridColDefFactory({ tColumn, t });
    const firstRow = results[0];
    const fields = Object.keys(firstRow);

    const columns = fields.map((field) => {
      const sampleValue = firstRow[field];
      const columnType = detectColumnType(sampleValue);

      // Try to use translation if available, otherwise format field name
      const headerName = tColumn(field, field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' '));

      const baseColumnConfig = {
        field,
        headerName,
        flex: 1,
        hideable: false,
        sortable: false,
      };

      switch (columnType.type) {
        case 'number':
          return createColumn({
            ...baseColumnConfig,
            minWidth: 120,
            type: 'number',
            align: GridAlignmentType.RIGHT,
            renderCell: cellFactory(NumberCell, {
              preset: columnType.isInteger ? NumberFormatPreset.INTEGER : NumberFormatPreset.DECIMAL,
            }),
          });

        case 'boolean':
          return createColumn({
            ...baseColumnConfig,
            minWidth: 100,
            type: 'boolean',
            align: GridAlignmentType.CENTER,
            renderCell: cellFactory(ActiveCell),
          });

        case 'array': {
          // Use ChipCell for simple arrays, ArrayCell for complex arrays
          const useChipCell = columnType.arrayItemType === 'string' || columnType.arrayItemType === 'number';
          return createColumn({
            ...baseColumnConfig,
            minWidth: 200,
            type: 'string',
            align: GridAlignmentType.LEFT,
            renderCell: useChipCell ? cellFactory(ChipCell) : cellFactory(ArrayCell),
          });
        }

        case 'object':
          return createColumn({
            ...baseColumnConfig,
            minWidth: 150,
            type: 'string',
            align: GridAlignmentType.LEFT,
            renderCell: cellFactory(ObjectCell),
          });

        default: // string
          return createColumn({
            ...baseColumnConfig,
            minWidth: 120,
            type: 'string',
            align: GridAlignmentType.LEFT,
            renderCell: cellFactory(EmptyFallbackCell),
          });
      }
    });

    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns,
    } as DataGridConfig<CypherResultRow>;
  }, [results, tColumn, t]);
};
